import type { TierIISnapshotMaxMode, TierIISnapshotAvgMode } from '../../tri-shared/types/db.ts';

/**
 * What a product's reportable amounts are derived from. The two sources carry genuinely different
 * data, so they are separate cases rather than one shape with holes in it:
 *
 * - `snapshots` holds each reading, so the highest and the entry count are readable from the list.
 * - `usageRecords` has already been aggregated to an annual total by the database, so the total
 *   alone cannot say how many entries produced it or how large the largest one was; both travel
 *   alongside it.
 */
export type TierIIInventoryReadings =
  | { source: 'snapshots'; amountsLbs: readonly number[] }
  | {
      source: 'usageRecords';
      /** Null when no usage was recorded at all, which is not the same as a recorded zero. */
      annualTotalLbs: number | null;
      /** Number of original usage entries that were summed into `annualTotalLbs`. */
      entryCount: number;
      /** Largest single original entry; null when it is unknown. */
      highestEntryLbs: number | null;
    };

export interface TierIISnapshotDerivationSettings {
  maxMode: TierIISnapshotMaxMode | null;
  avgMode: TierIISnapshotAvgMode | null;
  safetyPct: number | null;
  backlogPeriods: number | null;
  avgDays: number | null;
  avgPercentOfMax: number | null;
}

/** A reading is usable only if it is a real, non-negative quantity of pounds. */
function isUsableAmount(amountLbs: number): boolean {
  return Number.isFinite(amountLbs) && amountLbs >= 0;
}

/**
 * Reduce either reading source to the three numbers every derivation mode works from.
 * Returns null when nothing usable was recorded, which is distinct from a recorded zero.
 */
function readingsSummarize(
  readings: TierIIInventoryReadings,
): { totalLbs: number; highestLbs: number | null; entryCount: number } | null {
  switch (readings.source) {
    case 'snapshots': {
      const amounts = readings.amountsLbs.filter(isUsableAmount);
      if (amounts.length === 0) return null;
      return {
        totalLbs: amounts.reduce((sum, amount) => sum + amount, 0),
        highestLbs: Math.max(...amounts),
        entryCount: amounts.length,
      };
    }
    case 'usageRecords': {
      const { annualTotalLbs } = readings;
      if (annualTotalLbs == null || !isUsableAmount(annualTotalLbs)) return null;
      return {
        totalLbs: annualTotalLbs,
        highestLbs: readings.highestEntryLbs,
        entryCount: readings.entryCount,
      };
    }
  }
}

/**
 * Derive reportable maximum and average pounds from inventory readings.
 * Shared by the client preview and every server-side reporting path.
 */
export function tierIISnapshotDerivedAmounts(
  readings: TierIIInventoryReadings,
  settings: TierIISnapshotDerivationSettings,
): { maxLbs: number | null; avgLbs: number | null } {
  const summary = readingsSummarize(readings);
  if (summary === null) return { maxLbs: null, avgLbs: null };
  const { totalLbs, highestLbs, entryCount } = summary;

  let maxLbs: number | null;
  switch (settings.maxMode ?? 'highest') {
    case 'total_over_period':
      maxLbs =
        settings.backlogPeriods != null && settings.backlogPeriods > 0 ? totalLbs / settings.backlogPeriods : null;
      break;
    case 'highest_plus_safety':
      maxLbs = highestLbs == null ? null : highestLbs * (1 + (settings.safetyPct ?? 0) / 100);
      break;
    case 'highest':
      maxLbs = highestLbs;
      break;
  }

  let avgLbs: number | null;
  switch (settings.avgMode ?? 'average_readings') {
    case 'total_over_days':
      avgLbs = settings.avgDays != null && settings.avgDays > 0 ? totalLbs / settings.avgDays : null;
      break;
    case 'percent_of_max':
      avgLbs =
        maxLbs != null && settings.avgPercentOfMax != null && settings.avgPercentOfMax >= 0
          ? maxLbs * (settings.avgPercentOfMax / 100)
          : null;
      break;
    case 'average_readings':
      avgLbs = Number.isInteger(entryCount) && entryCount > 0 ? totalLbs / entryCount : null;
      break;
  }

  return {
    maxLbs: maxLbs == null ? null : Math.round(maxLbs),
    avgLbs: avgLbs == null ? null : Math.round(avgLbs),
  };
}
